"use client"

import { useState, useEffect, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { apiService } from "../../services/apiService"

const StudentAssignments = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [assignments, setAssignments] = useState([])
  const [selectedAssignment, setSelectedAssignment] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [feedback, setFeedback] = useState("")
  const [grade, setGrade] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  // Get student ID from URL query parameters
  const studentId = new URLSearchParams(location.search).get('student_id')
  console.log('=== StudentAssignments Component ===');
  console.log('Student ID from URL:', studentId);
  console.log('Current location:', location);

  // Define loadAssignments with useCallback
  const loadAssignments = useCallback(async () => {
    console.log('=== Starting loadAssignments ===');
    try {
      setIsLoading(true)
      setError("")
      console.log('Calling API to get assignments...');
      const data = await apiService.getStudentSubmissions(studentId)
      console.log('Assignments data received:', data);
      
      if (!data || !Array.isArray(data)) {
        console.error('Invalid assignments data received:', data);
        setError('Invalid response from server')
        setAssignments([])
        return
      }
      
      setAssignments(data)
    } catch (error) {
      console.error('Error in loadAssignments:', error);
      console.error('Error stack:', error.stack);
      setError(error.message || 'Failed to load assignments')
      setAssignments([])
    } finally {
      setIsLoading(false)
      console.log('=== End loadAssignments ===');
    }
  }, [studentId]); // Add studentId as dependency

  // First useEffect to load assignments when studentId changes
  useEffect(() => {
    if (studentId) {
      console.log('Loading assignments for student:', studentId);
      loadAssignments();
    } else {
      console.error('No student ID provided in URL');
      setError("No student ID provided")
      setIsLoading(false)
    }
  }, [studentId, loadAssignments]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not submitted';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }

  const handleViewAssignment = (assignment) => {
    console.log('Viewing assignment:', assignment);
    setSelectedAssignment(assignment);
  }

  const handleSubmitFeedback = async (e) => {
    e.preventDefault()
    if (!selectedAssignment) return

    console.log('=== Starting handleSubmitFeedback ===');
    console.log('Selected assignment:', selectedAssignment);
    console.log('Feedback:', feedback);
    console.log('Grade:', grade);

    try {
      setIsLoading(true)
      setError("")
      
      const feedbackData = {
        feedback,
        grade,
        status: "reviewed"
      }
      console.log('Submitting feedback data:', feedbackData);

      await apiService.submitAssignmentFeedback(selectedAssignment.id, feedbackData)
      console.log('Feedback submitted successfully');
      setSuccessMessage("Feedback submitted successfully!")
      
      // Clear form
      setFeedback("")
      setGrade("")
      
      // Refresh assignments
      console.log('Refreshing assignments list...');
      await loadAssignments()
      
      // Close submission details
      setSelectedAssignment(null)
    } catch (error) {
      console.error('Error in handleSubmitFeedback:', error);
      console.error('Error stack:', error.stack);
      setError(error.message || 'Failed to submit feedback')
    } finally {
      setIsLoading(false)
      console.log('=== End handleSubmitFeedback ===');
    }
  }

  if (isLoading) {
    return <div className="loading">Loading assignments...</div>
  }

  if (error) {
    return <div className="error-message">{error}</div>
  }

  console.log('Rendering assignments list. Number of assignments:', assignments.length);
  console.log('Assignments data:', assignments);

  return (
    <div className="student-assignments-page">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          <i className="material-icons">arrow_back</i> Back to Students
        </button>
        <h2>Student Assignments</h2>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {selectedAssignment ? (
        <div className="submission-details">
          <div className="submission-header">
            <h3>Assignment Details</h3>
            <button className="btn btn-secondary" onClick={() => setSelectedAssignment(null)}>
              <i className="material-icons">close</i>
            </button>
          </div>

          <div className="submission-content">
            <div className="submission-info">
              <p><strong>Title:</strong> {selectedAssignment.title}</p>
              <p><strong>Submitted:</strong> {formatDate(selectedAssignment.submitted_at)}</p>
              <p><strong>Status:</strong> {selectedAssignment.status}</p>
              {selectedAssignment.grade && (
                <p><strong>Grade:</strong> {selectedAssignment.grade}</p>
              )}
            </div>

            <div className="submission-text">
              <h4>Assignment Content</h4>
              <p>{selectedAssignment.content || selectedAssignment.text || 'No content provided'}</p>
            </div>

            {selectedAssignment.status !== "reviewed" && (
              <form onSubmit={handleSubmitFeedback} className="feedback-form">
                <h4>Provide Feedback</h4>
                <div className="form-group">
                  <label htmlFor="feedback">Feedback</label>
                  <textarea
                    id="feedback"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    className="form-control"
                    required
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="grade">Grade</label>
                  <select
                    id="grade"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select Grade</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                    <option value="F">F</option>
                  </select>
                </div>

                <button type="submit" className="btn" disabled={isLoading}>
                  {isLoading ? "Submitting..." : "Submit Feedback"}
                </button>
              </form>
            )}
          </div>
        </div>
      ) : (
        <div className="assignments-list">
          {assignments.length > 0 ? (
            assignments.map((assignment) => {
              console.log('Rendering assignment:', assignment);
              return (
                <div className="assignment-card" key={assignment.id}>
                  <div className="assignment-info">
                    <h3>{assignment.video_title || assignment.title}</h3>
                    <p className="assignment-date">
                      Submitted: {formatDate(assignment.created_at)}
                    </p>
                    <p className="assignment-status">
                      Status: <span className={`status-badge status-${assignment.status}`}>
                        {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
                      </span>
                    </p>
                    {assignment.grade && (
                      <p className="assignment-grade">
                        Grade: <span className="grade-badge">{assignment.grade}</span>
                      </p>
                    )}
                    <div className="assignment-content">
                      <p>{assignment.submission || 'No content provided'}</p>
                    </div>
                    <div className="assignment-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleViewAssignment(assignment)}
                      >
                        <i className="material-icons">feedback</i>
                        Provide Feedback
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-results">
              <p>No assignments found for this student.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default StudentAssignments 